import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import { useReflectionStore, type Reflection } from "../../hooks/useReflectionStore";
import { exploreClasses } from "../../mocks/explore";

const QUESTIONS_LABELS = [
  { key: "q1", icon: "✨", label: "인상 깊었던 것" },
  { key: "q2", icon: "🤔", label: "어려웠던 부분" },
  { key: "q3", icon: "🚀", label: "활용 아이디어" },
];

function ReflectionCard({ ref: _r, reflection }: { ref?: unknown; reflection: Reflection }) {
  const [expanded, setExpanded] = useState(false);
  const cls = exploreClasses.find((c) => c.id === reflection.classId);
  const scoreColor = reflection.avgScore >= 90 ? "#00D873" : reflection.avgScore >= 80 ? "#FF8C00" : "#E91E63";
  const hasContent = reflection.q1 || reflection.q2 || reflection.q3;

  return (
    <div className="bg-white rounded-2xl overflow-hidden border border-[#F2F4F6] hover:border-[#00D873]/20 transition-all">
      <div className="p-5">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: cls?.iconBg ?? "#F2F4F6" }}>
            <i className={`${cls?.icon ?? "ri-book-line"} text-base`} style={{ color: cls?.iconColor ?? "#8B95A1" }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-extrabold text-[#191F28] truncate">{reflection.classTitle}</p>
            <p className="text-[11px] text-[#8B95A1] mt-0.5">{reflection.date}</p>
          </div>
          <span className="text-sm font-black flex-shrink-0" style={{ color: scoreColor }}>
            {reflection.avgScore}점
          </span>
        </div>

        {/* Preview of q1 */}
        {reflection.q1 && !expanded && (
          <div className="bg-[#F9F9F9] rounded-xl p-3 mb-3">
            <p className="text-[11px] font-bold text-[#8B95A1] mb-1">✨ 인상 깊었던 것</p>
            <p className="text-xs text-[#3D4756] leading-relaxed line-clamp-2">{reflection.q1}</p>
          </div>
        )}

        {/* Expanded */}
        {expanded && (
          <div className="space-y-3 mb-3">
            {QUESTIONS_LABELS.map(({ key, icon, label }) => {
              const val = reflection[key as keyof Reflection] as string;
              return val ? (
                <div key={key} className="bg-[#F9F9F9] rounded-xl p-3">
                  <p className="text-[11px] font-bold text-[#8B95A1] mb-1">{icon} {label}</p>
                  <p className="text-xs text-[#3D4756] leading-relaxed">{val}</p>
                </div>
              ) : null;
            })}
          </div>
        )}

        {/* Toggle expand */}
        {hasContent && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full text-xs font-bold text-[#8B95A1] hover:text-[#191F28] transition-colors cursor-pointer py-1 flex items-center justify-center gap-1 whitespace-nowrap"
          >
            {expanded ? "접기" : "전체 보기"}
            <i className={`${expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-sm`} />
          </button>
        )}

        {!hasContent && (
          <p className="text-xs text-[#C8CDD4] text-center py-1">성찰 내용을 건너뛰었어요</p>
        )}
      </div>
    </div>
  );
}

export default function JournalPage() {
  const { reflections } = useReflectionStore();
  const [search, setSearch] = useState("");
  const [filterClass, setFilterClass] = useState("all");

  const classOptions = useMemo(() => {
    const ids = [...new Set(reflections.map((r) => r.classId))];
    return ids.map((id) => {
      const cls = exploreClasses.find((c) => c.id === id);
      return { id: String(id), label: cls?.title ?? `클래스 ${id}` };
    });
  }, [reflections]);

  const filtered = useMemo(() => {
    return reflections.filter((r) => {
      const matchClass = filterClass === "all" || String(r.classId) === filterClass;
      const matchSearch = !search || [r.classTitle, r.q1, r.q2, r.q3].some((v) => v?.toLowerCase().includes(search.toLowerCase()));
      return matchClass && matchSearch;
    });
  }, [reflections, search, filterClass]);

  // Group by date
  const grouped = useMemo(() => {
    const map = new Map<string, Reflection[]>();
    filtered.forEach((r) => {
      const group = map.get(r.date) ?? [];
      group.push(r);
      map.set(r.date, group);
    });
    return [...map.entries()];
  }, [filtered]);

  const totalAvg = useMemo(() => {
    if (!reflections.length) return 0;
    return Math.round(reflections.reduce((s, r) => s + r.avgScore, 0) / reflections.length);
  }, [reflections]);

  return (
    <div className="min-h-screen bg-[#F2F4F6] font-sans">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 pt-24 pb-16">

        {/* Hero */}
        <div className="bg-[#191F28] rounded-3xl p-7 mb-6 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-40 h-40 bg-[#00D873]/10 rounded-full translate-x-14 -translate-y-14 pointer-events-none" />
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs font-bold bg-[#00D873]/20 text-[#00D873] px-3 py-1.5 rounded-full">📓 학습 성찰 일지</span>
            </div>
            <h1 className="text-2xl font-black text-white mb-1">나의 학습 기록</h1>
            <p className="text-white/50 text-sm">스스로 정리한 생각들이 쌓여 진짜 실력이 돼요</p>
            <div className="flex gap-4 mt-4">
              <div className="text-center">
                <div className="text-xl font-black text-[#00D873]">{reflections.length}</div>
                <div className="text-white/40 text-[11px]">성찰 일지</div>
              </div>
              <div className="w-px bg-white/10" />
              <div className="text-center">
                <div className="text-xl font-black text-[#FF8C00]">{totalAvg || "-"}{totalAvg ? "점" : ""}</div>
                <div className="text-white/40 text-[11px]">평균 점수</div>
              </div>
              <div className="w-px bg-white/10" />
              <div className="text-center">
                <div className="text-xl font-black text-white">{classOptions.length}</div>
                <div className="text-white/40 text-[11px]">학습 클래스</div>
              </div>
            </div>
          </div>
        </div>

        {/* Search + Filter */}
        <div className="space-y-3 mb-6">
          <div className="relative">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-[#8B95A1] text-sm" />
            <input
              type="text"
              placeholder="일지 내용 검색..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-white rounded-2xl pl-10 pr-4 py-3 text-sm text-[#191F28] outline-none border-2 border-transparent focus:border-[#00D873] transition-colors placeholder:text-[#C8CDD4]"
            />
          </div>

          {classOptions.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setFilterClass("all")}
                className={`text-xs font-bold px-3.5 py-1.5 rounded-full transition-all cursor-pointer whitespace-nowrap ${filterClass === "all" ? "bg-[#191F28] text-white" : "bg-white text-[#8B95A1] hover:text-[#191F28]"}`}
              >전체</button>
              {classOptions.map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => setFilterClass(opt.id)}
                  className={`text-xs font-bold px-3.5 py-1.5 rounded-full transition-all cursor-pointer whitespace-nowrap ${filterClass === opt.id ? "bg-[#191F28] text-white" : "bg-white text-[#8B95A1] hover:text-[#191F28]"}`}
                >
                  {opt.label.slice(0, 10)}{opt.label.length > 10 ? ".." : ""}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Content */}
        {reflections.length === 0 ? (
          <div className="bg-white rounded-3xl p-12 text-center">
            <div className="w-16 h-16 rounded-2xl bg-[#F2F4F6] flex items-center justify-center mx-auto mb-4">
              <i className="ri-book-open-line text-3xl text-[#C8CDD4]" />
            </div>
            <h3 className="text-lg font-extrabold text-[#191F28] mb-2">아직 성찰 일지가 없어요</h3>
            <p className="text-sm text-[#8B95A1] mb-5">클래스를 완료하면 AI 피드백 후 성찰 일지를 쓸 수 있어요</p>
            <Link to="/explore" className="inline-block bg-[#00D873] text-white text-sm font-bold px-7 py-3.5 rounded-full cursor-pointer hover:opacity-90 whitespace-nowrap">
              클래스 시작하기 →
            </Link>
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl p-8 text-center">
            <p className="text-sm text-[#8B95A1]">검색 결과가 없어요</p>
          </div>
        ) : (
          <div className="space-y-6">
            {grouped.map(([date, items]) => (
              <div key={date}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-2 h-2 rounded-full bg-[#00D873] flex-shrink-0" />
                  <span className="text-xs font-extrabold text-[#8B95A1]">{date}</span>
                  <div className="flex-1 h-px bg-[#E4E6EA]" />
                  <span className="text-[11px] text-[#C8CDD4]">{items.length}개</span>
                </div>
                <div className="space-y-3">
                  {items.map((r) => <ReflectionCard key={r.id} reflection={r} />)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
